/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useState } from 'react';

import { Box, CircularProgress, TextField, Typography } from '@material-ui/core';

import { Autocomplete } from '@mui/material';
import PropTypes from 'prop-types';
import { contigoService } from 'services';

import { useStyles } from './styles';

export default function StockSearchAsync({
  value,
  onChange,
  label = 'Part Number',
  placeholder = 'Digite o part number para buscar no estoque...',
}) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const searchStock = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setOptions([]);
      return;
    }

    try {
      setLoading(true);
      const response = await contigoService.listContigoStock({
        search: searchText,
        skip: 0,
        take: 20,
      });

      const stockRecords = response.data || [];

      const groupedStock = {};
      stockRecords.forEach((record) => {
        if (!groupedStock[record.part_number]) {
          groupedStock[record.part_number] = {
            part_number: record.part_number,
            description: record.description || '',
            total_quantity: 0,
            locations: [],
            records: [],
          };
        }
        groupedStock[record.part_number].total_quantity += record.quantity || 0;
        groupedStock[record.part_number].locations.push(record.location);
        groupedStock[record.part_number].records.push(record);
      });

      const stockOptions = Object.values(groupedStock);
      setOptions(Array.isArray(stockOptions) ? stockOptions : []);
    } catch (error) {
      console.error('Erro ao buscar estoque:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchStock(inputValue);
    }, 800);

    return () => clearTimeout(timer);
  }, [inputValue, searchStock]);

  return (
    <Box className={classes.container}>
      {label && (
        <Typography variant='body2' color='textSecondary' gutterBottom>
          {label}
        </Typography>
      )}

      <Autocomplete
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.part_number || '';
        }}
        filterOptions={(x) => x}
        loading={loading}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant='outlined'
            size='small'
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component='li' {...props}>
            <Box style={{ width: '100%' }}>
              <Typography variant='body1'>
                <strong>{option.part_number}</strong>
              </Typography>
              {option.description && (
                <Typography variant='body2' color='textSecondary'>
                  {option.description}
                </Typography>
              )}
              <Box display='flex' gap={2} flexWrap='wrap' mt={0.5}>
                <Typography variant='body2' style={{ color: '#2E7D32', fontWeight: 500 }}>
                  Estoque: {option.total_quantity} unidades
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  Locais: {[...new Set(option.locations)].join(', ')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Digite pelo menos 2 caracteres para buscar'
            : 'Nenhum estoque encontrado'
        }
      />
    </Box>
  );
}

StockSearchAsync.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};
